import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { AuditLogService, type AuditLogEntry, type AuditScope } from './AuditLogService';

const WINDOW = 1000;

/**
 * Loads a bounded, most-recent window of the audit trail (SAAS-29/34) for either
 * the tenant or operator lens; the MegaGrid on top owns search, filtering, sort
 * and paging. `scope` is set once via `init`. `truncated` flags when older rows
 * fall outside the fetched window.
 */
@injectable()
export class AuditLogViewModel extends BaseViewModel {
    scope: AuditScope = 'tenant';
    entries: AuditLogEntry[] = [];
    total = 0;
    loading = true;
    error: string | null = null;
    private initialized = false;

    constructor(private readonly service: AuditLogService) {
        super();
    }

    init(scope: AuditScope): void {
        if (this.initialized) return;
        this.initialized = true;
        this.scope = scope;
        void this.load();
    }

    get truncated(): boolean {
        return this.total > this.entries.length;
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const page = await this.service.list(this.scope, { limit: WINDOW, offset: 0 });
            this.entries = page.entries;
            this.total = page.total;
        } catch (e) {
            this.entries = [];
            this.total = 0;
            this.error = e instanceof Error ? e.message : 'Failed to load audit log';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
